import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Badge,
  Flex,
  FormControl,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { searchJobLogs } from "../redux/actions/jobActions"
import { formatAUDate, formatAUTime } from "../global-client"
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons"
import { Link as ReactLink } from "react-router-dom"
import { useColorModeValue as mode } from "@chakra-ui/react"

const JobLogSearchScreen = () => {
  const dispatch = useDispatch()
  const { loading, error, jobLogs } = useSelector((state) => state.job)
  const { jobLogSearchPagination } = useSelector((state) => state.job)
  const [searchValue, setSearchValue] = useState("")
  const [hasSearched, setHasSearched] = useState(false)

  const handleSearch = () => {
    if (searchValue.trim()) {
      dispatch(searchJobLogs(searchValue, 1, 50))
      setHasSearched(true)
    }
  }

  const handlePageChange = (newPage) => {
    if (searchValue.trim()) {
      dispatch(searchJobLogs(searchValue, newPage, 50))
    }
  }

  return (
    <Box>
      {/* Search Form */}
      <Box
        maxW={{ base: "100%", md: "5xl" }}
        mx="auto"
        px={{ base: "2", md: "6", lg: "2" }}
        py={{ base: "2", md: "4", lg: "6" }}
        w={{ base: "95vw", md: "auto" }}
        borderWidth={1}
        borderRadius="md"
        mb={4}
      >
        <FormControl>
          <Flex
            direction={{ base: "column", md: "row" }}
            spacing={{ base: 2, md: 4 }}
            justify="center"
            align={{ base: "stretch", md: "center" }}
          >
            <Input
              flex={{ base: "1", md: "4" }}
              type="text"
              placeholder="Search by Title or Description..."
              py={{ base: "2", md: "2" }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch()
                }
              }}
            />
            <Button
              flex={{ base: "1", md: "1" }}
              minW={{ base: "auto", md: "150px" }}
              colorScheme="cyan"
              onClick={handleSearch}
              isLoading={loading}
            >
              Search
            </Button>
          </Flex>
          <Text fontSize="sm" color="gray.600" mt={2} textAlign="center">
            Searches Title and Description fields for job log entries.
          </Text>
        </FormControl>
      </Box>

      {/* Loading Spinner */}
      {loading && (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="md" mr={2} />
          <Text>Searching job logs…</Text>
        </Flex>
      )}

      {/* Error Alert */}
      {error && !loading && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {!loading && hasSearched && jobLogs && jobLogs.length > 0 && (
        <Box maxW={{ base: "100%", md: "6xl" }} mx="auto" px={{ base: "2", md: "6" }} py={{ base: "2", md: "4" }}>
          <Box mb={4}>
            <Text fontSize="lg" fontWeight="bold">
              Found {jobLogs.length} job log entries
            </Text>
          </Box>
          <Box overflowX={{ base: "auto", md: "auto" }} w="100%">
            <Box w="100%" display="flex" flexDirection="column" borderWidth="1px" borderRadius="lg">
              {/* Header Row */}
              <Flex
                bg={mode("gray.50", "gray.700")}
                borderBottom="1px solid"
                borderColor={mode("gray.200", "gray.600")}
                fontSize="sm"
                fontWeight="bold"
              >
                <Box flex="0.8" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Job No
                </Box>
                <Box flex="1.5" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Date
                </Box>
                <Box flex="0.8" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Time
                </Box>
                <Box flex="1" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Status
                </Box>
                <Box flex="2.5" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Title
                </Box>
                <Box flex="3" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                  Description
                </Box>
                <Box flex="0.7" p={2}>
                  Photos
                </Box>
              </Flex>

              {/* Data Rows */}
              {jobLogs
                .slice()
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((log, index) => (
                  <Flex
                    key={log._id}
                    bg={index % 2 === 0 ? mode("white", "gray.800") : mode("gray.100", "gray.700")}
                    borderBottom="1px solid"
                    borderColor={mode("gray.200", "gray.600")}
                    fontSize="sm"
                    align="center"
                  >
                    <Box flex="0.8" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <ReactLink to={`/job/${log.jobNo}`}>
                        <Text color="blue.500" _hover={{ textDecoration: "underline" }} cursor="pointer" fontWeight="semibold">
                          {log.jobNo}
                        </Text>
                      </ReactLink>
                    </Box>
                    <Box flex="1.5" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <Text>{log.date && formatAUDate(log.date)}</Text>
                    </Box>
                    <Box flex="0.8" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <Text>{log.date && formatAUTime(log.date)}</Text>
                    </Box>
                    <Box flex="1" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <Text>{log.status}</Text>
                    </Box>
                    <Box flex="2.5" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={log.title}>
                        {log.title}
                      </Text>
                    </Box>
                    <Box flex="3" p={2} borderRight="1px solid" borderColor={mode("gray.200", "gray.600")}>
                      <Text whiteSpace="normal" overflow="hidden" maxH="3rem" overflowY="auto" title={log.description}>
                        {log.description || "-"}
                      </Text>
                    </Box>
                    <Box flex="0.7" p={2} textAlign="center">
                      {log.photoCount && log.photoCount > 0 ? (
                        <Badge colorScheme="blue" fontSize="xs">
                          📷 {log.photoCount}
                        </Badge>
                      ) : (
                        <Text color={mode("gray.400", "gray.500")}>-</Text>
                      )}
                    </Box>
                  </Flex>
                ))}
            </Box>
          </Box>
          {/* Pagination Controls */}
          {jobLogSearchPagination && jobLogSearchPagination.totalPages > 1 && (
            <Flex justify="center" align="center" gap={4} mt={6} mb={4}>
              <Button
                leftIcon={<ChevronLeftIcon />}
                isDisabled={jobLogSearchPagination.currentPage === 1}
                onClick={() => handlePageChange(jobLogSearchPagination.currentPage - 1)}
                variant="outline"
              >
                Previous
              </Button>
              <Text fontSize="sm" fontWeight="semibold">
                Page {jobLogSearchPagination.currentPage} of {jobLogSearchPagination.totalPages} ({jobLogSearchPagination.totalCount} total)
              </Text>
              <Button
                rightIcon={<ChevronRightIcon />}
                isDisabled={jobLogSearchPagination.currentPage === jobLogSearchPagination.totalPages}
                onClick={() => handlePageChange(jobLogSearchPagination.currentPage + 1)}
                variant="outline"
              >
                Next
              </Button>
            </Flex>
          )}
        </Box>
      )}
      {!loading && hasSearched && jobLogs && jobLogs.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>No results</AlertTitle>
          <AlertDescription>No job log entries found matching "{searchValue}"</AlertDescription>
        </Alert>
      )}

      {/* Initial State */}
      {!hasSearched && (
        <Box maxW={{ base: "100%", md: "5xl" }} mx="auto" px={{ base: "2", md: "6" }} py={{ base: "4", md: "8" }}>
          <Text textAlign="center" color="gray.600" fontSize="md">
            Enter a word or phrase to search job log titles and descriptions.
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default JobLogSearchScreen
